import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import OutletEditor from "./pages/OutletEditor";
import OutletQR from "./pages/OutletQR";
import OutletPoster from "./pages/OutletPoster";
import OutletAnalytics from "./pages/OutletAnalytics";
import RedeemPage from "./pages/RedeemPage";
import FastRedeem from "./pages/FastRedeem";
import AppSettings from "./pages/AppSettings";
import AppQR from "./pages/AppQR";
import AppAnalytics from "./pages/AppAnalytics";
import AppOutletConfig from "./pages/AppOutletConfig";
import CustomerLanding from "./pages/customer/CustomerLanding";
import CustomerSpin from "./pages/customer/CustomerSpin";
import CustomerReward from "./pages/customer/CustomerReward";

// Admin pages
import AdminGuard from "./components/admin/AdminGuard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOwners from "./pages/admin/AdminOwners";
import AdminOwnerDetail from "./pages/admin/AdminOwnerDetail";
import AdminOutlets from "./pages/admin/AdminOutlets";
import AdminOutletDetail from "./pages/admin/AdminOutletDetail";
import AdminActivity from "./pages/admin/AdminActivity";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminHealth from "./pages/admin/AdminHealth";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminLeads from "./pages/admin/AdminLeads";
import MaintenancePage from "./pages/MaintenancePage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="tag">Loading…</div>
      </div>
    );
  }
  if (!user) {
    // Store intended destination for post-login redirect
    const redirectPath = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }
  return children;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppContent() {
  const [maintenance, setMaintenance] = React.useState(null);
  const { user } = useAuth();
  const loc = useLocation();

  // Whitelisted routes that work even during maintenance
  const AUTH_ROUTES = ["/login", "/register", "/forgot-password", "/reset-password", "/"];
  const isAuthRoute = AUTH_ROUTES.includes(loc.pathname);

  // Check public status on mount and route changes
  React.useEffect(() => {
    // Skip check for admin routes, auth routes, and the maintenance page itself
    if (loc.pathname.startsWith("/admin") || loc.pathname === "/maintenance" || isAuthRoute) return;
    
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/status`)
      .then(r => r.json())
      .then(d => {
        if (d.maintenance_mode) {
          setMaintenance(d);
        } else {
          setMaintenance(null);
        }
      })
      .catch(() => setMaintenance(null));
  }, [loc.pathname, isAuthRoute]);

  // Non-admin, non-auth users see maintenance page
  if (maintenance && !user?.is_admin && !isAuthRoute) {
    return <MaintenancePage settings={maintenance} />;
  }

  return (
    <>
      <ScrollTop />
      <Toaster position="top-center" richColors />
      <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/outlet/new" element={<ProtectedRoute><OutletEditor /></ProtectedRoute>} />
              <Route path="/outlet/:id" element={<ProtectedRoute><OutletEditor /></ProtectedRoute>} />
              <Route path="/outlet/:id/qr" element={<ProtectedRoute><OutletQR /></ProtectedRoute>} />
              <Route path="/outlet/:id/poster" element={<ProtectedRoute><OutletPoster /></ProtectedRoute>} />
              <Route path="/outlet/:id/analytics" element={<ProtectedRoute><OutletAnalytics /></ProtectedRoute>} />
              <Route path="/outlet/:id/redeem" element={<ProtectedRoute><RedeemPage /></ProtectedRoute>} />
            {/* Mobile app routes for quick redemption */}
            <Route path="/app" element={<Navigate to="/app/redeem" replace />} />
            <Route path="/app/redeem" element={<ProtectedRoute><FastRedeem /></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><AppSettings /></ProtectedRoute>} />
            <Route path="/app/qr" element={<ProtectedRoute><AppQR /></ProtectedRoute>} />
            <Route path="/app/analytics" element={<ProtectedRoute><AppAnalytics /></ProtectedRoute>} />
            <Route path="/app/outlet-config" element={<ProtectedRoute><AppOutletConfig /></ProtectedRoute>} />
            {/* Redirect old config route to settings */}
            <Route path="/app/config" element={<Navigate to="/app/settings" replace />} />
            {/* Legacy route - redirect to new app route */}
            <Route path="/app/entry" element={<Navigate to="/app/redeem" replace />} />
              {/* Customer-facing public routes */}
              <Route path="/r/:slug" element={<CustomerLanding />} />
              <Route path="/r/:slug/spin" element={<CustomerSpin />} />
              <Route path="/r/:slug/reward/:code" element={<CustomerReward />} />

              {/* Admin routes */}
              <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
              <Route path="/admin/owners" element={<AdminGuard><AdminOwners /></AdminGuard>} />
              <Route path="/admin/owners/:ownerId" element={<AdminGuard><AdminOwnerDetail /></AdminGuard>} />
              <Route path="/admin/outlets" element={<AdminGuard><AdminOutlets /></AdminGuard>} />
              <Route path="/admin/outlets/:outletId" element={<AdminGuard><AdminOutletDetail /></AdminGuard>} />
              <Route path="/admin/activity" element={<AdminGuard><AdminActivity /></AdminGuard>} />
              <Route path="/admin/analytics" element={<AdminGuard><AdminAnalytics /></AdminGuard>} />
              <Route path="/admin/subscriptions" element={<AdminGuard><AdminSubscriptions /></AdminGuard>} />
              <Route path="/admin/health" element={<AdminGuard><AdminHealth /></AdminGuard>} />
              <Route path="/admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
              <Route path="/admin/leads" element={<AdminGuard><AdminLeads /></AdminGuard>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;